/* eslint-disable react-hooks/exhaustive-deps */
import { UserOutput } from '@/api/gql/graphql';
import { useUsers } from '@/api/hooks';
import { ColumnDefinition } from '@/components/Ui/DataTable/DataTable.type';
import { useState, useCallback, useEffect } from 'react';
import iconUserDetail from '@/assets/icons/detail-user.svg';
import { useUserDetail } from '@/api/hooks/user/useUsers';
import { Checkbox } from '@/components/Common';
import { useUsersStore } from '@/store/UsersStore/useUsersStore';
import { useFilterStore } from '@/store/useFilterOptionsStore';
import { useUserStatistics } from '@/api/hooks/user/useUserStatistics';

export const useUsersList = () => {
  const [searchPhoneNumber, setSearchPhoneNumber] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const { filterSelections, setIsOpenFilter } = useFilterStore();
  const {
    appliedFilters,
    setAppliedFilters,
    isOpenUsersModal,
    openUsersModal,
    usersListlimit,
    setUsersListLimit,
    usersListPage,
    setUsersListPage,
  } = useUsersStore();

  const { isLoading: loadingStats } = useUserStatistics({from: null, to: null});

  const handleUserSelection = useCallback((id: string) => {
    setSelectedUsers((prev) =>
      prev.includes(id)
        ? prev.filter((userId) => userId !== id)
        : [...prev, id],
    );
  }, []);

  const getFilterValue = (value?: string[]) => (value?.length ? value : null);

  const variables = {
    filter: {
      limit: usersListlimit,
      page: usersListPage,
      phoneNumbers:
        searchPhoneNumber[0]?.length >= 13 ? searchPhoneNumber : null,
      schools: getFilterValue(appliedFilters.school),
      classes: getFilterValue(appliedFilters.class),
      gender:
        appliedFilters?.gender?.length === 2
          ? null
          : appliedFilters.gender?.[0],
      regions: getFilterValue(appliedFilters.region),
      series: getFilterValue(appliedFilters.serie),
    },
  };

  const {
    data: usersData,
    isLoading: lodingDataList,
    error,
  } = useUsers(variables.filter);

  useEffect(() => {
    if (Object.keys(filterSelections).length === 0) {
      setAppliedFilters({});
    }
  }, [filterSelections, setAppliedFilters]);

  const { data: userDetail } = useUserDetail(selectedUserId);

  const handleSearch = useCallback((value: string) => {
    setSearchPhoneNumber([`+221${value}`]);
  }, []);

  const handleUserDetail = useCallback(
    (userId: string) => {
      setSelectedUserId(userId);
      openUsersModal();
    },
    [openUsersModal],
  );

  const applyFilters = useCallback(() => {
    setAppliedFilters(filterSelections);
  }, [filterSelections, setAppliedFilters]);

  const columns: ColumnDefinition[] = [
    { field: 'student', header: 'Eléve' },
    { field: 'class', header: 'Classe' },
    { field: 'region', header: 'Région' },
    { field: 'school', header: 'Ecole' },
    { field: 'gender', header: 'Genre' },
    { field: 'sheet', header: 'Fiche' },
  ];

  const formattedData = usersData?.data?.map((user: UserOutput) => ({
    _id: user._id,
    student: (
      <div className="flex items-center">
        <Checkbox
          onClick={() => handleUserSelection(user._id)}
          checked={selectedUsers.includes(user._id)}
          value={user._id}
        />
        <span className="ml-4">
          {user.firstName} {user.lastName}
          <span className="block text-[#616163]">{user.phoneNumber}</span>
        </span>
      </div>
    ),
    class: `${user.class?.name} ${user.serie?.name || ''}`,
    region: user.region?.name || 'Non spécifié',
    gender: user.gender,
    school: user.school?.name || 'Non spécifiée',
    sheet: (
      <span
        role="img"
        className="w-1/3 block cursor-pointer"
        aria-label="Icone utilisateur"
        onClick={() => handleUserDetail(user._id)}
      >
        <img src={iconUserDetail} alt="Détails" />
      </span>
    ),
  }));

  return {
    columns,
    formattedData,
    usersData,
    lodingDataList,
    loadingStats,
    error,
    handleSearch,
    userDetail,
    selectedUserId,
    isOpenUsersModal,
    applyFilters,
    usersListlimit,
    setUsersListLimit,
    usersListPage,
    setUsersListPage,
    filterSelections,
    setIsOpenFilter,
  };
};
